import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import AdminLayout from './AdminLayout';

const C = {
  blue: '#6699ff',
  blueDark: '#4d7ae6',
  navy: '#0b1120',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

export default function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error) setArticles(data || []);
    setLoading(false);
  };

  const deleteArticle = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    await supabase.from('article_files').delete().eq('article_id', id);
    await supabase.from('articles').delete().eq('id', id);
    setArticles(articles.filter(a => a.id !== id));
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    const updates = { status: newStatus };
    if (newStatus === 'published') updates.published_at = new Date().toISOString();

    await supabase.from('articles').update(updates).eq('id', id);
    setArticles(articles.map(a => a.id === id ? { ...a, ...updates } : a));
  };

  const filtered = filter === 'all' ? articles : articles.filter(a => a.status === filter);
  const publishedCount = articles.filter(a => a.status === 'published').length;
  const draftCount = articles.filter(a => a.status === 'draft').length;

  return (
    <AdminLayout title="Articles">
      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          ['Total Articles', articles.length],
          ['Published', publishedCount],
          ['Drafts', draftCount],
        ].map(([label, value]) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.creamWarm}`, padding: '24px 28px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>{label}</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 600, color: C.navy }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'published', 'draft'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '8px 20px', border: `1px solid ${filter === f ? C.blue : C.creamWarm}`,
                background: filter === f ? 'rgba(102,153,255,0.08)' : C.white,
                color: filter === f ? C.blue : C.textBody,
                fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 600,
                letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {f} {f === 'all' ? `(${articles.length})` : f === 'published' ? `(${publishedCount})` : `(${draftCount})`}
            </button>
          ))}
        </div>

        <button
          onClick={() => navigate('/spc-portal-1003/articles/new')}
          style={{
            padding: '12px 28px', background: C.blue, color: C.navy, border: 'none',
            fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          + New Article
        </button>
      </div>

      {/* Article List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 16, color: C.textMuted, marginBottom: 16 }}>No articles yet</div>
          <button
            onClick={() => navigate('/spc-portal-1003/articles/new')}
            style={{
              padding: '12px 28px', background: C.blue, color: C.navy, border: 'none',
              fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
              letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            Create Your First Article
          </button>
        </div>
      ) : (

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(article => (
            <div key={article.id} style={{
              background: C.white, border: `1px solid ${C.creamWarm}`, padding: '20px 28px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              transition: 'all 0.2s ease',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <span style={{
                    padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    background: article.status === 'published' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
                    color: article.status === 'published' ? '#16a34a' : '#ca8a04',
                    borderRadius: 2,
                  }}>
                    {article.status}
                  </span>
                  {article.category && (
                    <span style={{ fontSize: 11, color: C.textMuted }}>{article.category}</span>
                  )}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.textDark, marginBottom: 4 }}>{article.title}</div>
                <div style={{ fontSize: 12, color: C.textMuted }}>
                  {new Date(article.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {article.tags?.length > 0 && ` · ${article.tags.join(', ')}`}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <ActionBtn label="Preview" onClick={() => navigate(`/spc-portal-1003/articles/${article.id}/preview`)} />
                <ActionBtn label="Edit" onClick={() => navigate(`/spc-portal-1003/articles/${article.id}/edit`)} />
                <ActionBtn
                  label={article.status === 'published' ? 'Unpublish' : 'Publish'}
                  onClick={() => toggleStatus(article.id, article.status)}
                  color={article.status === 'published' ? '#ca8a04' : '#16a34a'}
                />
                <ActionBtn label="Delete" onClick={() => deleteArticle(article.id, article.title)} color="#dc2626" />
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function ActionBtn({ label, onClick, color = '#3a4860' }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', border: `1px solid ${color}22`, background: `${color}08`,
        color: color, fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 600,
        letterSpacing: 0.5, cursor: 'pointer', transition: 'all 0.2s ease', borderRadius: 2,
      }}
      onMouseEnter={e => { e.target.style.background = `${color}15`; }}
      onMouseLeave={e => { e.target.style.background = `${color}08`; }}
    >
      {label}
    </button>
  );
}